import { useMemo } from "react";

import {
  NAV_GROUP_NAMES,
  NAV_V3_ITEMS,
  TODOS_NAV_ITEM,
} from "@/components/navigation/primary/constants";
import { PrimaryNavigationItemCollapsableWithIconI } from "@/components/navigation/primary/interfaces";
import Router from "@/helpers/router";
import { useFetchDailyTodos } from "@/modules/daily-todos/data/queries";
import { useDailyTodosViewer } from "@/modules/daily-todos/data/use-daily-todos-viewer";
import { getRemaining } from "@/modules/daily-todos/lib/schedule";

/** "To-Dos" with the count left today, in the Leads menu. */
const todosNavItem = (
  remaining: number
): PrimaryNavigationItemCollapsableWithIconI =>
  remaining > 0
    ? {
        ...TODOS_NAV_ITEM,
        text: (
          <span className="flex w-full items-center justify-between gap-3">
            <span>To-Dos</span>
            <span className="rounded-full bg-[#CC3997] px-1.5 tabular-nums text-white typography-body-5-semibold">
              {remaining}
            </span>
          </span>
        ),
      }
    : TODOS_NAV_ITEM;

/**
 * The Leads menu with To-Dos between Open pipeline and History (at the end
 * when Open pipeline isn't there).
 */
export const withTodosNavItem = (
  leads: PrimaryNavigationItemCollapsableWithIconI,
  remaining: number
): PrimaryNavigationItemCollapsableWithIconI => {
  const children = [...(leads.children ?? [])];
  const openPipelineIndex = children.findIndex(
    (child) => child.href === Router.accounts()
  );
  const item = todosNavItem(remaining);

  if (openPipelineIndex === -1) {
    children.push(item);
  } else {
    children.splice(openPipelineIndex + 1, 0, item);
  }

  return { ...leads, children };
};

/**
 * The Leads menu, with To-Dos for callers while the backend's
 * `daily_todos_enabled` is on (C-16 S6). Fails closed: not a caller, loading,
 * off (404 + 1035) and any error all leave the menu as before.
 */
export const useLeadsNavItem =
  (): PrimaryNavigationItemCollapsableWithIconI => {
    const { isCaller } = useDailyTodosViewer();
    const { data } = useFetchDailyTodos(isCaller);
    const isOn = isCaller && data?.isSwitchedOn === true;
    const remaining = getRemaining(data?.todos ?? null);
    const leads = NAV_V3_ITEMS[NAV_GROUP_NAMES.LEADS];

    return useMemo(
      () => (isOn ? withTodosNavItem(leads, remaining) : leads),
      [isOn, leads, remaining]
    );
  };
